import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  IconButton,
  Grid,
  Card,
  CardMedia,
  Typography,
  Stack,
  Alert,
  LinearProgress,
  Chip,
} from "@mui/material";
import { CloudUpload, Close } from "@mui/icons-material";
import { processImages, CompressionResult } from "../../utils/imageCompression";

interface PhotoUploadProps {
  photos: string[];
  onPhotosChange: (
    photos: string[],
    files?: File[],
    deletedPhotos?: string[]
  ) => void;
  maxPhotos?: number;
  disabled?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  photos,
  onPhotosChange,
  maxPhotos = 5,
  disabled = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [deletedPhotos, setDeletedPhotos] = useState<string[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlsRef = useRef<string[]>([]);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      setError(null);
      setCompressing(true);
      setCompressionProgress({ current: 0, total: files.length });

      try {
        // Check total photo limit first
        const totalPhotos =
          photos.length + newFiles.length + files.length - deletedPhotos.length;
        if (totalPhotos > maxPhotos) {
          setError(`Maximum ${maxPhotos} photos allowed`);
          return;
        }

        // Process images with compression if needed
        const results: CompressionResult[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setCompressionProgress({ current: i + 1, total: files.length });

          try {
            // Validate file type
            if (!file.type.startsWith("image/")) {
              throw new Error(`"${file.name}" is not an image file`);
            }

            // Process the image (compress if needed)
            const processedResults = await processImages([file], {
              maxSizeMB: 5, // 5MB limit
              quality: 1.0,
              maxWidth: 1920,
              maxHeight: 1080,
            });

            results.push(...processedResults);

            // Check if compressed file is still too large
            const processedFile = processedResults[0].file;
            if (processedFile.size > 5 * 1024 * 1024) {
              throw new Error(
                `Image "${file.name}" is too large even after compression. Please try a smaller image.`
              );
            }
          } catch (processingError: unknown) {
            const errorMessage =
              processingError instanceof Error
                ? processingError.message
                : "Unknown processing error";
            throw new Error(errorMessage);
          }
        }

        // Extract processed files
        const processedFiles = results.map((result) => result.file);

        // Add processed files to preview
        const updatedFiles = [...newFiles, ...processedFiles];
        setNewFiles(updatedFiles);

        // Create preview URLs for processed files
        const newPreviewUrls = processedFiles.map((file) =>
          URL.createObjectURL(file)
        );
        const allPhotos = [
          ...photos.filter((url) => !deletedPhotos.includes(url)),
          ...newPreviewUrls,
        ];

        onPhotosChange(allPhotos, updatedFiles, deletedPhotos);

        // Clear input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Log compression results
        const compressedImages = results.filter((r) => r.compressionRatio > 0);
        if (compressedImages.length > 0) {
          console.log(`🗜️ Compressed ${compressedImages.length} image(s)`);
          compressedImages.forEach((result) => {
            console.log(
              `  - ${result.file.name}: ${result.compressionRatio.toFixed(1)}% reduction`
            );
          });
        }
      } catch (error) {
        console.error("File processing error:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An error occurred while processing the images. Please try again.";
        setError(errorMessage);
      } finally {
        setCompressing(false);
        setCompressionProgress(null);
      }
    },
    [photos, newFiles, deletedPhotos, maxPhotos, onPhotosChange]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleFileSelect(files);
  };

  const handleDeletePhoto = (photoUrl: string, index: number) => {
    // Check if this is an existing photo or a new file
    const existingPhotoIndex = photos.findIndex(
      (p) => p === photoUrl && !deletedPhotos.includes(p)
    );

    if (existingPhotoIndex !== -1) {
      // This is an existing photo, mark for deletion
      const updatedDeletedPhotos = [...deletedPhotos, photoUrl];
      setDeletedPhotos(updatedDeletedPhotos);

      const remainingPhotos = photos.filter(
        (url) => !updatedDeletedPhotos.includes(url)
      );
      const newFileUrls = newFiles.map((file) => URL.createObjectURL(file));
      const allPhotos = [...remainingPhotos, ...newFileUrls];

      onPhotosChange(allPhotos, newFiles, updatedDeletedPhotos);
    } else {
      // This is a new file, remove from newFiles
      const newFileIndex = index - (photos.length - deletedPhotos.length);
      if (newFileIndex >= 0) {
        const updatedFiles = newFiles.filter((_, i) => i !== newFileIndex);
        setNewFiles(updatedFiles);

        // Revoke the object URL to prevent memory leaks
        URL.revokeObjectURL(photoUrl);

        const remainingPhotos = photos.filter(
          (url) => !deletedPhotos.includes(url)
        );
        const newFileUrls = updatedFiles.map((file) =>
          URL.createObjectURL(file)
        );
        const allPhotos = [...remainingPhotos, ...newFileUrls];

        onPhotosChange(allPhotos, updatedFiles, deletedPhotos);
      }
    }
  };

  const handleUploadClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      handleFileSelect(files);
    },
    [handleFileSelect]
  );

  // Get all current photos (existing + new)
  const currentPhotos = [
    ...photos.filter((url) => !deletedPhotos.includes(url)),
    ...newFiles.map((file) => URL.createObjectURL(file)),
  ];

  const canAddMore = currentPhotos.length < maxPhotos && !disabled;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="label"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontSize: "1rem",
          }}
        >
          Photos
        </Typography>
        <Chip
          label={`${currentPhotos.length}/${maxPhotos}`}
          size="small"
          sx={{
            backgroundColor:
              currentPhotos.length > 0 ? "primary.main" : "grey.200",
            color: currentPhotos.length > 0 ? "white" : "text.secondary",
            fontWeight: 600,
            borderRadius: 2,
            height: 20,
            fontSize: "0.75rem",
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {compressing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {compressionProgress
              ? `Compressing images... (${compressionProgress.current}/${compressionProgress.total})`
              : "Compressing images..."}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Modern Upload Area */}
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleUploadClick}
        sx={{
          border: `2px dashed ${isDragActive ? "#1976d2" : canAddMore ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: 2,
          backgroundColor: isDragActive
            ? "rgba(25, 118, 210, 0.08)"
            : canAddMore
              ? "rgba(25, 118, 210, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: canAddMore ? "pointer" : "default",
          opacity: canAddMore ? 1 : 0.6,
          "&:hover": canAddMore
            ? {
                borderColor: "#1565c0",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                transform: "translateY(-1px)",
              }
            : {},
          p: currentPhotos.length === 0 ? 4 : 2,
        }}
      >
        {currentPhotos.length === 0 ? (
          // Empty state - larger, more prominent
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CloudUpload sx={{ fontSize: 28, color: "primary.main" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
                fontSize: "1rem",
              }}
            >
              Upload your photos
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 1,
                fontSize: "0.85rem",
              }}
            >
              Drag and drop your images here, or click to browse
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                fontStyle: "italic",
              }}
            >
              Supports JPG, PNG
            </Typography>
          </Box>
        ) : (
          // Compact state when photos exist
          <Box sx={{ textAlign: "center" }}>
            <CloudUpload
              sx={{ fontSize: 20, color: "primary.main", mb: 0.5 }}
            />
            <Typography
              variant="body2"
              sx={{
                color: canAddMore ? "primary.main" : "text.secondary",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {canAddMore
                ? "Add more photos"
                : `Maximum ${maxPhotos} photos reached`}
            </Typography>
            {canAddMore && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  display: "block",
                  mt: 0.25,
                }}
              >
                Drag & drop or click to browse
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Photo Grid */}
      {currentPhotos.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={1.5}>
            {currentPhotos.map((photoUrl, index) => (
              <Grid item xs={6} sm={4} md={3} key={`photo-${index}`}>
                <Card
                  sx={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
                      "& .delete-button": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={photoUrl}
                    alt={`Photo ${index + 1}`}
                    sx={{
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {!disabled && (
                    <IconButton
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photoUrl, index);
                      }}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        color: "white",
                        opacity: 0,
                        transform: "scale(0.8)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(244, 67, 54, 0.9)",
                          transform: "scale(1)",
                        },
                        width: 28,
                        height: 28,
                      }}
                    >
                      <Close sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleInputChange}
        style={{ display: "none" }}
        disabled={disabled}
      />
    </Box>
  );
};

export default PhotoUpload;
